import { useState } from 'react';
import { toast } from 'sonner';

import { enhancedApi } from '@/services/api/policeDepartmentsService';

import { DepartmentHeader } from './DepartmentHeader';
import { DepartmentSidebarEdit } from './DepartmentSidebarEdit';
import { DepartmentSidebarView } from './DepartmentSidebarView';
import { EnrichmentBadge } from './EnrichmentBadge';
import { DepartmentSidebarProps } from './types';
import { useDepartmentForm } from './useDepartmentForm';

export const DepartmentSidebar = ({ department }: DepartmentSidebarProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { formData, setFormData, resetForm, handleSave, isLoading } =
    useDepartmentForm(department);
  const [triggerEnrichment, { isLoading: isEnriching }] =
    enhancedApi.usePoliceDepartmentsTriggerEnrichmentMutation();

  const handleRetry = async () => {
    try {
      await triggerEnrichment({ id: department.id }).unwrap();
      toast.success('Department enrichment started');
    } catch (error) {
      toast.error('Failed to start enrichment');
    }
  };

  const handleSaveAndClose = async () => {
    const success = await handleSave();
    if (success) {
      setIsEditing(false);
    }
    return success;
  };

  const handleCancel = () => {
    setIsEditing(false);
    resetForm();
  };

  if (isEditing) {
    return (
      <DepartmentSidebarEdit
        formData={formData}
        onFormDataChange={(data) => setFormData(data)}
        onSave={handleSaveAndClose}
        onCancel={handleCancel}
        isLoading={isLoading}
      />
    );
  }

  return (
    <div className="rounded-lg border bg-white p-5 shadow-sm">
      <DepartmentHeader
        name={department.name || ''}
        jurisdiction={formData.jurisdictionName}
        stateCode={formData.stateCode}
      />

      <div className="mt-3 border-b pb-3">
        <DepartmentSidebarView
          formData={formData}
          onEdit={() => setIsEditing(true)}
        />
      </div>

      <div className="pt-3">
        <EnrichmentBadge
          status={department.enrichment_status}
          onRetry={handleRetry}
          isEnriching={isEnriching}
        />
      </div>
    </div>
  );
};
